"use client"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Header from "../Components/Header"
import axios from "axios"

const Notification = () => {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [unreadCount, setUnreadCount] = useState(0)
  const [activeTab, setActiveTab] = useState("all")
  const token = localStorage.getItem("jwttoken")
  const url = "https://kaasbackend.vetrinahealthcare.com/"

  useEffect(() => {
    fetchNotifications()
  }, [])

  // Fetch LMS & Social Notifications
  const fetchNotifications = async () => {
    setLoading(true)
    try {
      const [lmsResponse, socialResponse] = await Promise.all([
        axios.get(`${url}lms/user/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${url}socialapp/notifications/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ])

      const lmsNotifications = lmsResponse.data.map((notif) => ({
        ...notif,
        source: "LMS",
        is_read: true,
      }))

      const socialNotifications = socialResponse.data.map((notif) => ({
        ...notif,
        source: "SocialApp",
        message: formatSocialMessage(notif),
      }))

      // Merge & Sort Notifications (Latest First)
      const mergedNotifications = [...lmsNotifications, ...socialNotifications].sort(
        (a, b) => new Date(b.created_at || b.date_sent) - new Date(a.created_at || a.date_sent),
      )

      setNotifications(mergedNotifications)
      setUnreadCount(socialNotifications.filter((n) => !n.is_read).length)
    } catch (error) {
      console.error("Error fetching notifications:", error)
    } finally {
      setLoading(false)
    }
  }

  // Format Social App Notifications
  const formatSocialMessage = (notif) => {
    switch (notif.type) {
      case "group_join_request":
        return `${notif.sender} requested to join a group.`
      case "group_join_approved":
        return `Your request to join the group was approved.`
      case "group_join":
        return `You were added to the group.`
      default:
        return notif.message || "You have a new notification."
    }
  }

  // Mark Notification as Read
  const markAsRead = async (id) => {
    try {
      await axios.post(
        `${url}socialapp/notifications/${id}/read/`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )

      // Update UI
      setNotifications((prev) => prev.map((notif) => (notif.id === id ? { ...notif, is_read: true } : notif)))
      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch (error) {
      console.error("Error marking notification as read:", error)
    }
  }

  // Filtered Notifications Based on Active Tab
  const filteredNotifications = notifications.filter((notif) => {
    if (activeTab === "all") return true
    if (activeTab === "lms") return notif.source === "LMS"
    if (activeTab === "social") return notif.source === "SocialApp"
    if (activeTab === "unread") return !notif.is_read
    if (activeTab === "read") return notif.is_read
    return true
  })

  // Get notification icon based on source and type
  const getNotificationIcon = (notification) => {
    if (notification.source === "LMS") {
      return (
        <div className="notification-icon lms">
          <i className="fas fa-book"></i>
        </div>
      )
    } else {
      switch (notification.type) {
        case "group_join_request":
          return (
            <div className="notification-icon social request">
              <i className="fas fa-user-plus"></i>
            </div>
          )
        case "group_join_approved":
          return (
            <div className="notification-icon social approved">
              <i className="fas fa-check-circle"></i>
            </div>
          )
        case "group_join":
          return (
            <div className="notification-icon social join">
              <i className="fas fa-users"></i>
            </div>
          )
        default:
          return (
            <div className="notification-icon social default">
              <i className="fas fa-bell"></i>
            </div>
          )
      }
    }
  }

  // Get time ago string
  const getTimeAgo = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const seconds = Math.floor((now - date) / 1000)

    if (seconds < 60) return "just now"

    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`

    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`

    const days = Math.floor(hours / 24)
    if (days < 7) return `${days} ${days === 1 ? "day" : "days"} ago`

    return date.toLocaleDateString()
  }

  // Handle back button click
  const handleBackClick = () => {
    navigate(-1); // Go back to previous page
  }

  return (
    <div className="notifications-page">
      <Header />
      <div className="container-fluid">
        <div className="row Profile_info_row p-0"></div>
      </div>

      <div className="notifications-wrapper">
        <div className="notifications-container">
          {/* Header with Back Button and Title */}
          <div className="page-header">
            <button className="back-button" onClick={handleBackClick}>
              <i className="fas fa-arrow-left"></i>
              Back
            </button>
            <h1 className="page-title">Notifications</h1>
            <div className="header-spacer"></div> {/* For proper alignment */}
          </div>

          {/* Tabs */}
          <div className="notification-tabs">
            <button className={`tab-button ${activeTab === "all" ? "active" : ""}`} onClick={() => setActiveTab("all")}>
              All
            </button>
            <button className={`tab-button ${activeTab === "lms" ? "active" : ""}`} onClick={() => setActiveTab("lms")}>
              LMS
            </button>
            <button
              className={`tab-button ${activeTab === "social" ? "active" : ""}`}
              onClick={() => setActiveTab("social")}
            >
              Social
            </button>
            <button
              className={`tab-button ${activeTab === "unread" ? "active" : ""}`}
              onClick={() => setActiveTab("unread")}
            >
              Unread
              {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
            </button>
            <button
              className={`tab-button ${activeTab === "read" ? "active" : ""}`}
              onClick={() => setActiveTab("read")}
            >
              Read
            </button>
          </div>

          {/* Notifications List */}
          <div className="notifications-list">
            {loading ? (
              <div className="loading-container">
                <div className="loader"></div>
                <p>Loading notifications...</p>
              </div>
            ) : filteredNotifications.length > 0 ? (
              filteredNotifications.map((notification, index) => (
                <div key={index} className={`notification-card ${!notification.is_read ? "unread" : ""}`}>
                  {getNotificationIcon(notification)}
                  <div className="notification-content">
                    <div className="notification-header">
                      <h3 className="notification-title">
                        {notification.source === "LMS" ? "Learning Management" : "Social Network"}
                      </h3>
                      <span className="notification-time">
                        {getTimeAgo(notification.created_at || notification.date_sent)}
                      </span>
                    </div>
                    <p className="notification-message">{notification.message || notification.text}</p>
                    {notification.source === "SocialApp" && !notification.is_read && (
                      <button onClick={() => markAsRead(notification.id)} className="mark-read-button">
                        Mark as Read
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <div className="empty-icon">
                  <i className="fas fa-bell-slash"></i>
                </div>
                <h3>No notifications found</h3>
                <p>You're all caught up! Check back later for new notifications.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CSS Styles */}
      <style jsx>{`
        .notifications-page {
          font-family: 'Poppins', sans-serif;
          background-color: #f8f9fa;
          min-height: 100vh;
        }

        .notifications-wrapper {
          padding: 20px;
          margin-top: 65px;
          display: flex;
          justify-content: center;
        }

        .notifications-container {
          width: 100%;
          max-width: 800px;
          background-color: white;
          border-radius: 16px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
          padding: 30px;
          overflow: hidden;
        }

        /* Page Header with Back Button */
        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 24px;
          position: relative;
        }

        .back-button {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #f8f9fa;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 500;
          color: #4b5563;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .back-button:hover {
          background: #e5e7eb;
          color: #374151;
          transform: translateX(-2px);
        }

        .back-button i {
          font-size: 12px;
        }

        .page-title {
          font-size: 28px;
          font-weight: 700;
          color: #333;
          text-align: center;
          flex: 1;
          margin: 0;
        }

        .header-spacer {
          width: 100px; /* Same width as back button for proper centering */
        }

        /* Tabs */
        .notification-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 30px;
          justify-content: center;
          border-bottom: 1px solid #eee;
          padding-bottom: 15px;
        }

        .tab-button {
          background: none;
          border: none;
          padding: 10px 20px;
          border-radius: 30px;
          font-size: 14px;
          font-weight: 600;
          color: #666;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
        }

        .tab-button:hover {
          color: #6366f1;
          background-color: #f5f5ff;
        }

        .tab-button.active {
          background-color: #6366f1;
          color: white;
        }

        .badge {
          position: absolute;
          top: -8px;
          right: -8px;
          background-color: #ef4444;
          color: white;
          font-size: 12px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* Notifications */
        .notifications-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .notification-card {
          display: flex;
          padding: 16px;
          border-radius: 12px;
          background-color: white;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
          transition: all 0.3s ease;
          border-left: 4px solid #e5e7eb;
        }

        .notification-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
        }

        .notification-card.unread {
          background-color: #f5f5ff;
          border-left: 4px solid #6366f1;
        }

        .notification-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 16px;
          flex-shrink: 0;
        }

        .notification-icon i {
          font-size: 20px;
          color: white;
        }

        .notification-icon.lms {
          background: linear-gradient(135deg, #22c55e, #16a34a);
        }

        .notification-icon.social.request {
          background: linear-gradient(135deg, #6366f1, #4f46e5);
        }

        .notification-icon.social.approved {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
        }

        .notification-icon.social.join {
          background: linear-gradient(135deg, #ec4899, #db2777);
        }

        .notification-icon.social.default {
          background: linear-gradient(135deg, #f97316, #ea580c);
        }

        .notification-content {
          flex: 1;
        }

        .notification-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
        }

        .notification-title {
          font-size: 15px;
          font-weight: 600;
          color: #333;
          margin: 0;
        }

        .notification-time {
          font-size: 12px;
          color: #6b7280;
        }

        .notification-message {
          font-size: 14px;
          color: #4b5563;
          margin: 0 0 10px 0;
          line-height: 1.5;
        }

        .mark-read-button {
          background-color: #6366f1;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }

        .mark-read-button:hover {
          background-color: #4f46e5;
        }

        /* Loading */
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 0;
        }

        .loader {
          border: 3px solid #f3f3f3;
          border-radius: 50%;
          border-top: 3px solid #6366f1;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 60px 0;
          color: #6b7280;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
          color: #d1d5db;
        }

        .empty-state h3 {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 8px;
          color: #4b5563;
        }

        .empty-state p {
          font-size: 14px;
          max-width: 300px;
          margin: 0 auto;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .notifications-container {
            padding: 20px;
            border-radius: 0;
            box-shadow: none;
          }

          .page-header {
            flex-direction: column;
            gap: 15px;
            align-items: flex-start;
          }

          .back-button {
            order: 1;
          }

          .page-title {
            order: 2;
            text-align: left;
            font-size: 24px;
          }

          .header-spacer {
            display: none;
          }

          .notification-tabs {
            justify-content: flex-start;
            overflow-x: auto;
            padding-bottom: 10px;
            gap: 5px;
          }

          .tab-button {
            padding: 8px 16px;
            font-size: 13px;
            white-space: nowrap;
          }

          .notification-card {
            padding: 12px;
          }

          .notification-icon {
            width: 40px;
            height: 40px;
          }
        }

        @media (max-width: 480px) {
          .notifications-wrapper {
            padding: 10px;
          }

          .page-title {
            font-size: 22px;
          }

          .back-button {
            padding: 6px 12px;
            font-size: 13px;
          }
        }
      `}</style>
    </div>
  )
}

export default Notification